// WebSocket client — connects to the game server, sends ClientMsg, dispatches ServerMsg.

import { SERVER_PORT, type ClientMsg, type ServerMsg } from "../shared/protocol.js";

type Handler = (msg: ServerMsg) => void;
type StatusHandler = (status: "connecting" | "open" | "closed") => void;

export class Net {
  private ws: WebSocket | null = null;
  private handlers: Handler[] = [];
  private statusHandlers: StatusHandler[] = [];
  private url: string;
  private reconnectMs = 1500;

  constructor(host?: string, port?: number) {
    if (!host) {
      // Auto-detect: in production, connect to same host over wss
      const loc = typeof window !== "undefined" ? window.location : undefined;
      const isSecure = loc?.protocol === "https:";
      const proto = isSecure ? "wss" : "ws";
      const h = loc?.hostname ?? "localhost";
      // In production (port 443/80), WS goes through Apache proxy on same port.
      // In dev, connect directly to the game server port.
      const p = (loc?.port && loc.port !== "443" && loc.port !== "80" && loc.port !== "")
        ? Number(loc.port)
        : (isSecure ? 443 : SERVER_PORT);
      this.url = `${proto}://${h}:${p}/ws`;
    } else {
      this.url = `ws://${host}:${port ?? SERVER_PORT}`;
    }
  }

  connect(): void {
    this.notifyStatus("connecting");
    this.ws = new WebSocket(this.url);
    this.ws.onopen = () => this.notifyStatus("open");
    this.ws.onclose = () => {
      this.notifyStatus("closed");
      setTimeout(() => this.connect(), this.reconnectMs);
    };
    this.ws.onerror = () => this.dispatch({ t: "error", reason: "socket_error" });
    this.ws.onmessage = (ev) => {
      try {
        this.dispatch(JSON.parse(ev.data as string) as ServerMsg);
      } catch {
        this.dispatch({ t: "error", reason: "bad_server_message" });
      }
    };
  }

  send(msg: ClientMsg): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  on(h: Handler): void { this.handlers.push(h); }
  onStatus(h: StatusHandler): void { this.statusHandlers.push(h); }

  private dispatch(msg: ServerMsg): void {
    for (const h of this.handlers) h(msg);
  }
  private notifyStatus(s: "connecting" | "open" | "closed"): void {
    for (const h of this.statusHandlers) h(s);
  }
}
